import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PackageReqFeesResponse } from '@/api/package-req/types/PkgReqFeesResponse'
import { PkgPricesResponse } from '@/api/payments/types/PkgPricesResponse'
import { TaxesData } from '@/api/payments/types/TaxesData'
import { PkgShippingMethod } from '@/api/user-package/types/UserPkg'
import { Spinner } from '@/components/ui/spinner'

import { ShippingOptions } from '../ShippingPage'
import { PaymentData } from './PaymentData'

interface Props {
  insurancePrice: number
  taxesData: TaxesData | undefined
  delayChargePrice: number
  taxFreeFeeUSD: number
  packageFees: PackageReqFeesResponse | undefined
  paymentData: PaymentData
  shippingOptions: ShippingOptions
  shippingPrices: PkgPricesResponse['shippingPrices']
  isLoadingTaxes: boolean
  totalPrice: number
  couponDiscountAmount: number
  creditToUseUSD: number
}
export const PaymentSummaryDetails = ({
  insurancePrice,
  taxesData,
  delayChargePrice,
  taxFreeFeeUSD,
  packageFees,
  paymentData,
  shippingOptions,
  shippingPrices,
  isLoadingTaxes,
  totalPrice,
  couponDiscountAmount,
  creditToUseUSD,
}: Props) => {
  const { t } = useTranslation()
  const [isTaxesDetailsShown, setIsTaxesDetailsShown] = useState(false)

  const shippingPrice =
    shippingOptions.selectedShippingMethod === PkgShippingMethod.Test ? 0.1 : (shippingPrices[shippingOptions.selectedShippingMethod] ?? 0)

  return (
    <div className='space-y-4'>
      <h3 className='text-text-primary text-sm font-medium'>{t('payment.summaryTotal')}</h3>
      <div className='border-border-default rounded-lg border bg-white p-4'>
        <div className='space-y-3'>
          {/* Shipping */}
          <div className='flex justify-between'>
            <span className='text-text-secondary text-sm'>{t('payment.shipping')}</span>
            <span className='text-text-primary text-sm font-medium'>{shippingPrice ? `$${shippingPrice}` : t('payment.notAvailable')}</span>
          </div>

          {/* Delay charge */}
          {!!delayChargePrice && delayChargePrice > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>{t('payment.storage')}</span>
              <span className='text-text-primary text-sm font-medium'>${delayChargePrice}</span>
            </div>
          )}

          {/* Insurance */}
          <div className='flex justify-between'>
            <span className='text-text-secondary text-sm'>{t('payment.insurance')}</span>
            <span className='text-text-primary text-sm font-medium'>${shippingOptions.isInsuranceAdded ? insurancePrice : 0}</span>
          </div>

          {/* Tax Free Fee */}
          {!!taxFreeFeeUSD && taxFreeFeeUSD > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>{t('payment.delawareTransfer')}</span>
              <span className='text-text-primary text-sm font-medium'>${taxFreeFeeUSD}</span>
            </div>
          )}

          {/* Unite Fee */}
          {!!packageFees?.uniteFee && packageFees.uniteFee > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>{t('payment.uniteFee')}</span>
              <span className='text-text-primary text-sm font-medium'>${packageFees.uniteFee}</span>
            </div>
          )}

          {/* Photo Request Fee */}
          {!!packageFees?.photoRequestFeeUSD && packageFees.photoRequestFeeUSD > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>{t('payment.photoFee')}</span>
              <span className='text-text-primary text-sm font-medium'>${packageFees.photoRequestFeeUSD}</span>
            </div>
          )}

          {/* Shrink Request Fee */}
          {!!packageFees?.shrinkRequestFeeUSD && packageFees.shrinkRequestFeeUSD > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>{t('payment.shrinkFee')}</span>
              <span className='text-text-primary text-sm font-medium'>${packageFees.shrinkRequestFeeUSD}</span>
            </div>
          )}

          {/* Taxes */}
          <div className='flex justify-between'>
            <span className='text-text-secondary flex items-center gap-1 text-sm'>
              {t('payment.taxesAdvance')}
              {taxesData && taxesData.sum > 0 && (
                <button
                  onClick={() => setIsTaxesDetailsShown(!isTaxesDetailsShown)}
                  className='text-text-link cursor-pointer hover:underline'
                >
                  {isTaxesDetailsShown ? t('payment.hideDetails') : t('payment.showDetails')}
                </button>
              )}
            </span>
            {isLoadingTaxes ? (
              <Spinner />
            ) : (
              <span className='text-text-primary text-sm font-medium'>${taxesData?.sum ? taxesData.sum.toFixed(2) : 0}</span>
            )}
          </div>

          {/* Taxes Details */}
          {isTaxesDetailsShown && taxesData?.details && (
            <div className='border-border-default space-y-2 rounded border bg-gray-50 p-3'>
              <div className='flex justify-between text-xs'>
                <span className='text-text-secondary'>{t('payment.vat')}</span>
                <span className='text-text-primary'>${taxesData.details.vat}</span>
              </div>
              <div className='flex justify-between text-xs'>
                <span className='text-text-secondary'>{t('payment.fees')}</span>
                <span className='text-text-primary'>${taxesData.details.externalFees}</span>
              </div>
              <div className='flex justify-between text-xs'>
                <span className='text-text-secondary'>{t('payment.releaseFee')}</span>
                <span className='text-text-primary'>${taxesData.details.shiptanbulFee}</span>
              </div>
            </div>
          )}

          {/* Israeli ID Input */}

          {/* Coupon Discount */}
          {couponDiscountAmount > 0 && (
            <div className='flex justify-between text-green-600'>
              <span className='text-sm'>{t('payment.couponLabel', { code: paymentData.couponCode })}</span>
              <span className='text-sm font-medium'>- ${couponDiscountAmount}</span>
            </div>
          )}

          {/* Credit applied */}
          {creditToUseUSD > 0 && (
            <div className='flex justify-between text-green-600'>
              <span className='text-sm'>{t('payment.creditUse')}</span>
              <span className='text-sm font-medium'>- ${creditToUseUSD}</span>
            </div>
          )}

          {/* Divider */}
          <div className='border-border-default border-t' />

          {/* Total */}
          <div className='flex justify-between'>
            <span className='text-text-primary text-base font-medium'>{t('payment.total')}</span>
            <span className='text-text-primary text-base font-medium'>${totalPrice}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
